import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { concat, get } from 'lodash';
import {
  IAssembly,
  IDaypart,
  IDefaultAssembly,
  IMappedLocationGroupDaypart,
} from 'src/interfaces';
import { generateQuery, queryKeys, requestCCApi } from 'src/utils';

import { mapDefaultAssemblies, mapLocationDefaultAssembly } from '../helper';

export const useGetDefaultAssemblies = <
  T extends string | undefined = undefined,
>(
  filters: string[] = [],
  enabled = true,
  type?: T,
  rbStoreIntegration?: boolean,
  currentDate?: string,
) => {
  const resolvedDate = currentDate ?? dayjs().format('YYYY-MM-DD');

  return useQuery({
    queryKey: queryKeys.defaultAssemblies.all([
      ...filters,
      type ?? '',
      rbStoreIntegration ?? false,
      resolvedDate,
    ]),
    queryFn: () =>
      fetchDefaultAssemblies(filters, type, rbStoreIntegration, resolvedDate),
    enabled,
  });
};

export const fetchDefaultAssemblies = async <
  T extends string | undefined = undefined,
>(
  filters: string[] = [],
  type?: T,
  rbStoreIntegration?: boolean,
  currentDate?: string,
): Promise<
  T extends 'timing' ? IDefaultAssembly[] : IMappedLocationGroupDaypart[]
> => {
  const currentDateFilter = currentDate ? [`currentDate=${currentDate}`] : [];

  const url = `/defaultAssemblies${generateQuery(
    rbStoreIntegration
      ? ['assemblies']
      : ['assemblies', 'dayparts', 'locationGroups'],
    concat(filters, currentDateFilter, 'pageSize=0'),
  )}`;

  const response = await requestCCApi({ url });

  if (type === 'timing') {
    return mapDefaultAssemblies(response) as any;
  } else {
    const defaultAssemblies = get(response, 'data', []);

    const assemblies = get(
      response,
      'includes.assemblies.data',
      [],
    ) as IAssembly[];
    const dayparts = get(response, 'includes.dayparts.data', []) as IDaypart[];

    return mapLocationDefaultAssembly(
      defaultAssemblies as IDefaultAssembly[],
      assemblies,
      dayparts,
    ) as any;
  }
};

export const useGetPaginatedDefaultAssemblies = (
  filters: string[] = [],

  enabled: boolean = true,
) => {
  const queryParams = concat(filters, [`&count=true`]);

  return useQuery({
    queryKey: queryKeys.defaultAssemblies.pagination(queryParams),
    enabled,
    queryFn: async () => {
      const response = await requestCCApi({
        url: `/defaultAssemblies${generateQuery(
          ['assemblies', 'dayparts'],
          queryParams,
        )}`,
      });

      const defaultAssemblies = get(response, 'data', []);
      const assemblies = get(
        response,
        'includes.assemblies.data',
        [],
      ) as IAssembly[];
      const dayparts = get(
        response,
        'includes.dayparts.data',
        [],
      ) as IDaypart[];

      const defaultAssemblyData = mapLocationDefaultAssembly(
        defaultAssemblies,
        assemblies,
        dayparts,
      );

      return {
        defaultAssemblyData,
        totalPages: get(response, 'totalPages', 0),
        totalCount: get(response, 'totalCount', 0),
      };
    },
  });
};
